import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { poisAPI, categoriesAPI, layersAPI, uploadAPI } from '../../services/api';
import { POI, Category, Layer, POI_STATUS, POI_STATUS_LABELS, POIStatus } from '../../types';

export const ManagePOIs: React.FC = () => {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [editingPOI, setEditingPOI] = useState<POI | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLayer, setSelectedLayer] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    layer: '',
    lat: -7.65,
    long: 110.8,
    address: '',
    contact: {
      phone: '',
      email: '',
      website: ''
    },
    video: '',
    status: POI_STATUS.PROSPECTIVE as POIStatus,
    links: [] as string[],
    tags: [] as string[],
    isActive: true,
    isFeatured: false
  });

  const [tagInput, setTagInput] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedImages, setUploadedImages] = useState<any[]>([]);
  const [imageUploadProgress, setImageUploadProgress] = useState<boolean>(false);

  // Fetch POIs with filters
  const { data: poisData, isLoading: poisLoading } = useQuery({
    queryKey: ['pois', searchQuery, selectedCategory, selectedLayer, currentPage],
    queryFn: () => poisAPI.getAll({
      search: searchQuery || undefined,
      category: selectedCategory || undefined,
      layer: selectedLayer || undefined,
      page: currentPage,
      limit: 10,
      isActive: undefined // Show all POIs in admin
    }),
  });

  // Fetch categories and layers for dropdowns
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesAPI.getAll(),
  });

  const { data: layersData } = useQuery({
    queryKey: ['layers'],
    queryFn: () => layersAPI.getAll(),
  });

  const pois = poisData?.data || [];
  const categories = categoriesData?.data || [];
  const layers = layersData?.data || [];
  const totalPOIs = poisData?.total || 0;
  const totalPages = Math.ceil(totalPOIs / 10);

  // Create POI mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => poisAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pois'] });
      setShowAddForm(false);
      resetForm();
      alert('POI created successfully!');
    },
    onError: (error: any) => {
      alert('Error creating POI: ' + (error.response?.data?.message || error.message));
    },
  });

  // Update POI mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => poisAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pois'] });
      setEditingPOI(null);
      resetForm();
      alert('POI updated successfully!');
    },
    onError: (error: any) => {
      alert('Error updating POI: ' + (error.response?.data?.message || error.message));
    },
  });

  // Upload images mutation
  const uploadImagesMutation = useMutation({
    mutationFn: (files: File[]) => {
      const promises = files.map(file => uploadAPI.single(file));
      return Promise.all(promises);
    },
    onSuccess: (results) => {
      const newImages = results.map(result => ({
        url: result.data?.url || '',
        publicId: result.data?.publicId || '',
        caption: '',
        isPrimary: false
      }));
      setUploadedImages(prev => [...prev, ...newImages]);
      setSelectedFiles([]);
      setImageUploadProgress(false);
    },
    onError: (error: any) => {
      setImageUploadProgress(false);
      alert('Error uploading images: ' + (error.response?.data?.message || error.message));
    },
  });

  // Delete POI mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => poisAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pois'] });
      alert('POI deleted successfully!');
    },
    onError: (error: any) => {
      alert('Error deleting POI: ' + (error.response?.data?.message || error.message));
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedLayer('');
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      layer: '',
      lat: -7.65,
      long: 110.8,
      address: '',
      contact: {
        phone: '',
        email: '',
        website: ''
      },
      video: '',
      status: POI_STATUS.PROSPECTIVE as POIStatus,
      links: [],
      tags: [],
      isActive: true,
      isFeatured: false
    });
    setTagInput('');
    setLinkInput('');
    setValidationErrors({});
    setSelectedFiles([]);
    setUploadedImages([]);
    setImageUploadProgress(false);
  };

  const handleEdit = (poi: POI) => {
    setFormData({
      name: poi.name,
      description: poi.description,
      category: typeof poi.category === 'object' ? poi.category._id : poi.category,
      layer: typeof poi.layer === 'object' ? poi.layer._id : poi.layer,
      lat: poi.lat,
      long: poi.long,
      address: poi.address || '',
      contact: {
        phone: poi.contact?.phone || '',
        email: poi.contact?.email || '',
        website: poi.contact?.website || ''
      },
      video: poi.video || '',
      status: poi.status || POI_STATUS.PROSPECTIVE,
      links: poi.links || [],
      tags: poi.tags || [],
      isActive: poi.isActive,
      isFeatured: poi.isFeatured
    });
    setUploadedImages(poi.images || []);
    setEditingPOI(poi);
    setShowAddForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.description.trim()) errors.description = 'Description is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.layer) errors.layer = 'District is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    setValidationErrors({});
    
    const submitData = {
      ...formData,
      images: uploadedImages
    };
    
    if (editingPOI) {
      updateMutation.mutate({ id: editingPOI._id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingPOI(null);
    resetForm();
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addLink = () => {
    if (linkInput.trim() && !formData.links.includes(linkInput.trim())) {
      // Basic URL validation
      if (!linkInput.startsWith('http://') && !linkInput.startsWith('https://')) {
        alert('Please enter a valid URL starting with http:// or https://');
        return;
      }
      setFormData(prev => ({
        ...prev,
        links: [...prev.links, linkInput.trim()]
      }));
      setLinkInput('');
    }
  };

  const removeLink = (linkToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter(link => link !== linkToRemove)
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const uploadImages = async () => {
    if (selectedFiles.length === 0) return;
    
    setImageUploadProgress(true);
    uploadImagesMutation.mutate(selectedFiles);
  };

  const removeUploadedImage = (indexToRemove: number) => {
    setUploadedImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const updateImageCaption = (index: number, caption: string) => {
    setUploadedImages(prev => prev.map((img, i) => 
      i === index ? { ...img, caption } : img
    ));
  };

  const toggleImagePrimary = (index: number) => {
    setUploadedImages(prev => prev.map((img, i) => ({
      ...img,
      isPrimary: i === index ? !img.isPrimary : false // Only one primary image
    })));
  };

  // Effect to close forms when POI data changes
  useEffect(() => {
    if (!showAddForm && !editingPOI) {
      resetForm();
    }
  }, [showAddForm, editingPOI]);

  if (poisLoading) return <div style={{ padding: '20px' }}>Loading POIs...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
        <h1>Manage Points of Interest</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => { setShowAddForm(true); setEditingPOI(null); }}
            style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
          >
            + Add POI
          </button>
          <Link to="/admin" style={{ color: '#3b82f6', textDecoration: 'none' }}>← Dashboard</Link>
          <button onClick={logout} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Logout</button>
        </div>
      </header>

      {/* Filters */}
      <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
        <h3 style={{ margin: '0 0 15px' }}>Filters</h3>
        <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Search POIs..."
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">All Categories</option>
              {categories.map((cat: Category) => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>District</label>
            <select
              value={selectedLayer}
              onChange={(e) => { setSelectedLayer(e.target.value); setCurrentPage(1); }}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="">All Districts</option>
              {layers.map((layer: Layer) => (
                <option key={layer._id} value={layer._id}>{layer.name}</option>
              ))}
            </select>
          </div>
          <div>
            <button
              onClick={resetFilters}
              style={{ background: '#6b7280', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* POIs List */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ background: '#f9fafb', padding: '15px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Points of Interest ({totalPOIs} total)</h3>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Page {currentPage} of {totalPages}
          </div>
        </div>
        
        {pois.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p>No POIs found. {searchQuery || selectedCategory || selectedLayer ? 'Try adjusting your filters.' : 'Generate sample data from the dashboard to get started.'}</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '1px', background: '#e5e7eb' }}>
              {pois.map((poi: POI) => {
                const category = typeof poi.category === 'object' ? poi.category : null;
                const layer = typeof poi.layer === 'object' ? poi.layer : null;
                
                return (
                  <div key={poi._id} style={{ background: 'white', padding: '20px', display: 'flex', gap: '20px' }}>
                    {/* POI Image */}
                    <div style={{ flexShrink: 0 }}>
                      {poi.images && poi.images.length > 0 ? (
                        <img
                          src={poi.images[0].url}
                          alt={poi.name}
                          style={{ width: '100px', height: '80px', objectFit: 'cover', borderRadius: '6px' }}
                        />
                      ) : (
                        <div style={{ width: '100px', height: '80px', background: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}>
                          No Image
                        </div>
                      )}
                    </div>
                    
                    {/* POI Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'start', gap: '15px', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 4px 0', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {poi.name}
                            {poi.isFeatured && <span style={{ background: '#fbbf24', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '12px' }}>FEATURED</span>}
                            {!poi.isActive && <span style={{ background: '#ef4444', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '12px' }}>INACTIVE</span>}
                          </h4>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            {category?.name || 'No Category'} • {layer?.name || 'No District'}
                          </div>
                          <p style={{ margin: '0 0 8px', color: '#374151', fontSize: '14px', lineHeight: '1.4' }}>
                            {poi.description.length > 150 ? poi.description.substring(0, 150) + '...' : poi.description}
                          </p>
                        </div>
                        
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button
                            onClick={() => window.open(`/?poi=${poi._id}`, '_blank')}
                            style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEdit(poi)}
                            style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(poi._id, poi.name)}
                            disabled={deleteMutation.isPending}
                            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {/* Additional Info */}
                      <div style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        {poi.address && <span>📍 {poi.address}</span>}
                        {poi.contact?.phone && <span>📞 {poi.contact.phone}</span>}
                        {poi.contact?.website && <span>🌐 Website</span>}
                        <span>⭐ {poi.rating.toFixed(1)} ({poi.reviewCount} reviews)</span>
                        <span>👁️ {poi.metadata?.views || 0} views</span>
                        {poi.images.length > 0 && <span>🖼️ {poi.images.length} images</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', gap: '10px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{ background: currentPage === 1 ? '#e5e7eb' : '#3b82f6', color: currentPage === 1 ? '#9ca3af' : 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: currentPage === 1 ? 'default' : 'pointer' }}
                >
                  Previous
                </button>
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 16px', color: '#374151' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{ background: currentPage === totalPages ? '#e5e7eb' : '#3b82f6', color: currentPage === totalPages ? '#9ca3af' : 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: currentPage === totalPages ? 'default' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit POI Form Modal */}
      {(showAddForm || editingPOI) && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '20px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ margin: '0 0 20px 0' }}>{editingPOI ? 'Edit POI' : 'Add New POI'}</h2>
            
            <form onSubmit={handleSubmit}>
              {/* Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  style={{ width: '100%', padding: '8px', border: validationErrors.name ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '4px' }}
                />
                {validationErrors.name && <span style={{ color: '#ef4444', fontSize: '12px' }}>{validationErrors.name}</span>}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  style={{ width: '100%', padding: '8px', border: validationErrors.description ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical' }}
                />
                {validationErrors.description && <span style={{ color: '#ef4444', fontSize: '12px' }}>{validationErrors.description}</span>}
              </div>

              {/* Category and Layer */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    style={{ width: '100%', padding: '8px', border: validationErrors.category ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat: Category) => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  {validationErrors.category && <span style={{ color: '#ef4444', fontSize: '12px' }}>{validationErrors.category}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>District *</label>
                  <select
                    value={formData.layer}
                    onChange={(e) => setFormData(prev => ({ ...prev, layer: e.target.value }))}
                    style={{ width: '100%', padding: '8px', border: validationErrors.layer ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value="">Select District</option>
                    {layers.map((layer: Layer) => (
                      <option key={layer._id} value={layer._id}>{layer.name}</option>
                    ))}
                  </select>
                  {validationErrors.layer && <span style={{ color: '#ef4444', fontSize: '12px' }}>{validationErrors.layer}</span>}
                </div>
              </div>

              {/* Address */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Address *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  style={{ width: '100%', padding: '8px', border: validationErrors.address ? '2px solid #ef4444' : '1px solid #d1d5db', borderRadius: '4px' }}
                />
                {validationErrors.address && <span style={{ color: '#ef4444', fontSize: '12px' }}>{validationErrors.address}</span>}
              </div>

              {/* Coordinates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={(e) => setFormData(prev => ({ ...prev, lat: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    placeholder="-7.65"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.long}
                    onChange={(e) => setFormData(prev => ({ ...prev, long: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    placeholder="110.8"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Contact Information</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.contact.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, phone: e.target.value } }))}
                    style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.contact.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, email: e.target.value } }))}
                    style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <input
                  type="url"
                  placeholder="Website URL"
                  value={formData.contact.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, website: e.target.value } }))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              {/* Video URL */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Video URL (optional)</label>
                <input
                  type="url"
                  placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                  value={formData.video}
                  onChange={(e) => setFormData(prev => ({ ...prev, video: e.target.value }))}
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  Add a YouTube, Vimeo, or other video URL to showcase this location
                </small>
              </div>

              {/* Status */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Investment Status</label>
                
                {/* Status 1-3: Dropdown */}
                <div style={{ marginBottom: '10px' }}>
                  <select
                    value={formData.status === POI_STATUS.NATIONAL_STRATEGIC ? POI_STATUS.PROSPECTIVE : formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: parseInt(e.target.value) as POIStatus }))}
                    disabled={formData.status === POI_STATUS.NATIONAL_STRATEGIC}
                    style={{ 
                      width: '100%', 
                      padding: '8px', 
                      border: '1px solid #d1d5db', 
                      borderRadius: '4px',
                      opacity: formData.status === POI_STATUS.NATIONAL_STRATEGIC ? 0.5 : 1
                    }}
                  >
                    <option value={POI_STATUS.PROSPECTIVE}>{POI_STATUS_LABELS[POI_STATUS.PROSPECTIVE]}</option>
                    <option value={POI_STATUS.POTENTIAL}>{POI_STATUS_LABELS[POI_STATUS.POTENTIAL]}</option>
                    <option value={POI_STATUS.IPRO}>{POI_STATUS_LABELS[POI_STATUS.IPRO]}</option>
                  </select>
                </div>
                
                {/* Status 4: Checkbox */}
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.status === POI_STATUS.NATIONAL_STRATEGIC}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, status: POI_STATUS.NATIONAL_STRATEGIC }));
                        } else {
                          setFormData(prev => ({ ...prev, status: POI_STATUS.PROSPECTIVE }));
                        }
                      }}
                    />
                    <span style={{ fontSize: '14px', color: '#374151' }}>
                      {POI_STATUS_LABELS[POI_STATUS.NATIONAL_STRATEGIC]}
                    </span>
                  </label>
                </div>
              </div>

              {/* Links */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Additional Links</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="url"
                    placeholder="Add website URL (https://...)"
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
                    style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                  <button
                    type="button"
                    onClick={addLink}
                    style={{ background: '#6b7280', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {formData.links.map((link, index) => (
                    <div
                      key={index}
                      style={{ background: '#f3f4f6', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                    >
                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeLink(link)}
                        style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '3px', padding: '2px 6px', cursor: 'pointer', fontSize: '10px' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tags</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    placeholder="Add tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    style={{ background: '#6b7280', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{ background: '#e5e7eb', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Images</label>
                
                {/* File Upload */}
                <div style={{ marginBottom: '10px' }}>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ marginBottom: '10px' }}
                  />
                  {selectedFiles.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {selectedFiles.length} file(s) selected
                      </span>
                      <button
                        type="button"
                        onClick={uploadImages}
                        disabled={imageUploadProgress}
                        style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        {imageUploadProgress ? 'Uploading...' : 'Upload Images'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Uploaded Images */}
                {uploadedImages.length > 0 && (
                  <div>
                    <h4 style={{ margin: '0 0 10px', fontSize: '14px' }}>Uploaded Images ({uploadedImages.length})</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                      {uploadedImages.map((image, index) => (
                        <div key={index} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '8px', background: '#f9fafb' }}>
                          <div style={{ position: 'relative', marginBottom: '8px' }}>
                            <img
                              src={image.url}
                              alt={`Upload ${index + 1}`}
                              style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                            <button
                              type="button"
                              onClick={() => removeUploadedImage(index)}
                              style={{ position: 'absolute', top: '2px', right: '2px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '10px' }}
                            >
                              ×
                            </button>
                            {image.isPrimary && (
                              <span style={{ position: 'absolute', top: '2px', left: '2px', background: '#10b981', color: 'white', fontSize: '8px', padding: '2px 4px', borderRadius: '2px' }}>
                                PRIMARY
                              </span>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Caption (optional)"
                            value={image.caption || ''}
                            onChange={(e) => updateImageCaption(index, e.target.value)}
                            style={{ width: '100%', padding: '4px', border: '1px solid #d1d5db', borderRadius: '3px', fontSize: '12px', marginBottom: '4px' }}
                          />
                          <button
                            type="button"
                            onClick={() => toggleImagePrimary(index)}
                            style={{ width: '100%', background: image.isPrimary ? '#10b981' : '#e5e7eb', color: image.isPrimary ? 'white' : '#374151', border: 'none', padding: '4px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}
                          >
                            {image.isPrimary ? 'Primary Image' : 'Set as Primary'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Checkboxes */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Active
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                  />
                  Featured
                </label>
              </div>

              {/* Form Actions */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{ background: '#6b7280', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}
                >
                  {editingPOI ? (updateMutation.isPending ? 'Updating...' : 'Update POI') : (createMutation.isPending ? 'Creating...' : 'Create POI')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
